import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Table,
  Button,
  Typography,
  Card,
  Spin,
  Modal,
  Input,
  InputNumber,
  Space,
  message,
  Popconfirm,
  Empty,
  Tag,
  Breadcrumb,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import {
  getBoothSKUs,
  createSKU,
  updateSKU,
  deleteSKU,
  SKU,
  MAX_SKUS_PER_BOOTH,
  MAX_SHORT_NAME_LENGTH,
} from '@festpix/services';
import { colors } from '@festpix/ui';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@festpix/services';

const { Title, Text } = Typography;

interface FormState {
  name: string;
  shortName: string;
  price: number;
  quantity: number;
  controlInventory: boolean;
}

const initialFormState: FormState = {
  name: '',
  shortName: '',
  price: 0,
  quantity: 0,
  controlInventory: true,
};

export default function BoothSKUsPage() {
  const navigate = useNavigate();
  const { boothId } = useParams<{ boothId: string }>();

  const [loading, setLoading] = useState(true);
  const [skus, setSKUs] = useState<SKU[]>([]);
  const [boothName, setBoothName] = useState<string>('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSKU, setEditingSKU] = useState<SKU | null>(null);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else if (boothId) {
        loadData();
      }
    });
    return unsubscribe;
  }, [navigate, boothId]);

  const loadData = async () => {
    if (!boothId) return;

    try {
      // Load booth name
      const boothDoc = await getDoc(doc(db, 'booths', boothId));
      if (boothDoc.exists()) {
        setBoothName(boothDoc.data().name);
      }

      // Load SKUs
      const skusData = await getBoothSKUs(boothId);
      setSKUs(skusData);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    if (skus.length >= MAX_SKUS_PER_BOOTH) {
      message.warning(`Máximo de ${MAX_SKUS_PER_BOOTH} SKUs por estande`);
      return;
    }
    setEditingSKU(null);
    setFormState(initialFormState);
    setModalVisible(true);
  };

  const handleOpenEditModal = (sku: SKU) => {
    setEditingSKU(sku);
    setFormState({
      name: sku.name,
      shortName: sku.shortName,
      price: sku.price,
      quantity: sku.quantity,
      controlInventory: sku.controlInventory,
    });
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingSKU(null);
    setFormState(initialFormState);
  };

  const handleSave = async () => {
    if (!formState.name.trim()) {
      message.error('Por favor, insira o nome do produto');
      return;
    }
    if (!formState.shortName.trim()) {
      message.error('Por favor, insira o nome curto');
      return;
    }
    if (formState.shortName.length > MAX_SHORT_NAME_LENGTH) {
      message.error(`Nome curto deve ter no máximo ${MAX_SHORT_NAME_LENGTH} caracteres`);
      return;
    }
    if (formState.price <= 0) {
      message.error('Preço deve ser maior que zero');
      return;
    }
    if (formState.controlInventory && formState.quantity < 0) {
      message.error('Quantidade não pode ser negativa');
      return;
    }

    setSaving(true);
    try {
      if (editingSKU) {
        await updateSKU(editingSKU.id, formState);
        setSKUs(skus.map((s) => (s.id === editingSKU.id ? { ...s, ...formState } : s)));
        message.success('SKU atualizado com sucesso!');
      } else if (boothId) {
        const newSKU = await createSKU(boothId, formState);
        setSKUs([...skus, newSKU]);
        message.success('SKU criado com sucesso!');
      }
      handleCloseModal();
    } catch (error: any) {
      message.error(error.message || 'Erro ao salvar SKU');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (skuId: string) => {
    try {
      await deleteSKU(skuId);
      setSKUs(skus.filter((s) => s.id !== skuId));
      message.success('SKU excluído com sucesso!');
    } catch (error: any) {
      message.error(error.message || 'Erro ao excluir SKU');
    }
  };

  const getStockStatus = (quantity: number, controlInventory: boolean) => {
    if (!controlInventory) {
      return <Tag color="blue">Ilimitado</Tag>;
    }
    if (quantity === 0) {
      return <Tag color="error">Esgotado</Tag>;
    }
    if (quantity <= 10) {
      return <Tag color="warning">Estoque Baixo</Tag>;
    }
    return <Tag color="success">Em Estoque</Tag>;
  };

  const columns: ColumnsType<SKU> = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Botão: {record.shortName}
          </Text>
        </div>
      ),
    },
    {
      title: 'Preço',
      dataIndex: 'price',
      key: 'price',
      render: (price) => (
        <Text style={{ color: colors.success, fontWeight: 500 }}>
          R$ {price.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: 'Estoque',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity, record) => (
        <Space>
          {record.controlInventory ? (
            <Text strong>{quantity}</Text>
          ) : (
            <Text strong>∞</Text>
          )}
          {getStockStatus(quantity, record.controlInventory)}
        </Space>
      ),
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(record)}
            style={{ background: colors.info }}
          >
            Editar
          </Button>
          <Popconfirm
            title="Excluir SKU"
            description="Tem certeza que deseja excluir este SKU?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Excluir
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate('/admin/booths')}>Estandes</a> },
          { title: boothName },
          { title: 'SKUs' },
        ]}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/booths')}
          >
            Voltar
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            <ShoppingOutlined style={{ marginRight: 12 }} />
            SKUs - {boothName}
          </Title>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={handleOpenCreateModal}
          style={{ background: colors.success }}
          disabled={skus.length >= MAX_SKUS_PER_BOOTH}
        >
          Adicionar SKU
        </Button>
      </div>

      <Card style={{ background: colors.cardBackground }}>
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            {skus.length} de {MAX_SKUS_PER_BOOTH} SKUs cadastrados
          </Text>
        </div>
        <Table
          columns={columns}
          dataSource={skus}
          rowKey="id"
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description="Nenhum SKU cadastrado"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={handleOpenCreateModal}>
                  Adicionar Primeiro SKU
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>

      <Modal
        title={
          <span>
            <ShoppingOutlined style={{ marginRight: 8, color: colors.success }} />
            {editingSKU ? 'Editar SKU' : 'Novo SKU'}
          </span>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editingSKU ? 'Salvar' : 'Criar'}
        cancelText="Cancelar"
        okButtonProps={{ style: { background: colors.success } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Nome do Produto:
            </label>
            <Input
              size="large"
              placeholder="Ex: Refrigerante Lata 350ml"
              value={formState.name}
              onChange={(e) => setFormState({ ...formState, name: e.target.value })}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              Nome completo exibido no cardápio
            </Text>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Nome Curto (Botão):
            </label>
            <Input
              size="large"
              placeholder="Ex: Refri Lata"
              value={formState.shortName}
              onChange={(e) => setFormState({ ...formState, shortName: e.target.value })}
              maxLength={MAX_SHORT_NAME_LENGTH}
              showCount
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              Nome exibido nos botões do app vendedor (máx. {MAX_SHORT_NAME_LENGTH} caracteres)
            </Text>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Preço (R$):
            </label>
            <InputNumber
              size="large"
              style={{ width: '100%' }}
              placeholder="0.00"
              value={formState.price}
              onChange={(value) => setFormState({ ...formState, price: value || 0 })}
              min={0.01}
              step={0.5}
              precision={2}
              prefix="R$"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Controlar Estoque:
            </label>
            <Space>
              <Switch
                checked={formState.controlInventory}
                onChange={(checked) => setFormState({ ...formState, controlInventory: checked })}
              />
              <Text type="secondary">
                {formState.controlInventory ? 'Estoque controlado' : 'Estoque ilimitado'}
              </Text>
            </Space>
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {formState.controlInventory
                ? 'Vendas serão bloqueadas quando o estoque acabar'
                : 'Vendas não serão limitadas pelo estoque'}
            </Text>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Quantidade em Estoque:
            </label>
            <InputNumber
              size="large"
              style={{ width: '100%' }}
              placeholder="0"
              value={formState.controlInventory ? formState.quantity : undefined}
              onChange={(value) => setFormState({ ...formState, quantity: value || 0 })}
              min={0}
              step={1}
              precision={0}
              disabled={!formState.controlInventory}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {formState.controlInventory
                ? 'Estoque será decrementado automaticamente a cada venda'
                : 'Campo desabilitado quando o estoque é ilimitado'}
            </Text>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
